import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const StCatharines = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/where-to-live-in-toronto/'

  const cheapestLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e5%a4%a7%e5%a4%9a%e4%bc%a6%e5%a4%9a%e4%bd%8f%e6%88%bf%e3%80%81%e7%a7%9f%e6%88%bf%e6%8c%87%e5%8d%97/'
    : 'https://liv.rent/blog/neighbourhoods/cheapest-places-to-rent-greater-toronto-area/'

  const getAbout = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.st-catharines:About St Catharines Paragraph 1">
            Nestled in{' '}
            <LinkText href="/rental-listings/ontario">Ontario’s</LinkText>{' '}
            Niagara Region, St. Catharines is a vibrant city with a blend of
            natural beauty and urban opportunities. The city is home to{' '}
            <LinkText href="/rental-listings/city/st-catharines/brock-university">
              Brock University
            </LinkText>
            , a prominent educational institution that enriches the community’s
            academic landscape. St. Catharines offers picturesque surroundings,
            including the scenic shores of Lake Ontario and the nearby Niagara
            Escarpment, providing residents with outdoor havens for relaxation
            and exploration. The city’s cultural scene thrives through various
            festivals, arts events, and historical sites that highlight its rich
            heritage.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.st-catharines:About St Catharines Paragraph 2">
            St. Catharines’ charm lies in its unique fusion of suburban comforts
            and proximity to the Greater Toronto Area. While not directly part
            of the GTA, it offers easy access to{' '}
            <LinkText href="/rental-listings/city/toronto">Toronto’s</LinkText>{' '}
            amenities, making it a desirable choice for those seeking a balanced
            lifestyle.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={neighborhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,300 - $2,300',
          bed2: '$1,500 - $2,800',
          bed3: '$2,200 - $3,200'
        }}
        rentReportCity={t('cities:city_name_toronto')}
        rentReportLink="https://liv.rent/blog/rent-reports/toronto/"
        rentReportLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/%e5%a4%9a%e4%bc%a6%e5%a4%9a/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.st-catharines:Apartment Hunting in St Catharines Paragraph 1">
            {{ cityName }} is an attractive city of Ontario known for its
            thriving community and diverse housing options. While rental prices
            in {{ cityName }} are generally more affordable compared to Toronto,
            they have been steadily increasing over time. As a result, finding{' '}
            <LinkText href={links.under1000}>
              cheap rentals in {{ cityName }}
            </LinkText>
            , especially{' '}
            <LinkText href={links.apartmentsUnder1000}>
              apartments for rent under $1000
            </LinkText>
            , can be challenging. However, to initiate your search, you can
            refer to this guide highlighting{' '}
            <LinkText href={cheapestLink}>
              affordable neighborhoods in the Greater Toronto Area (GTA)
            </LinkText>{' '}
            that may offer relatively more budget-friendly rental options.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.st-catharines:Apartments for rent St Catharines Paragraph">
              <LinkText href={links.apartment}>
                Apartments for rent in St. Catharines
              </LinkText>{' '}
              are quite popular among young professionals, students, and smaller
              households. Renters should expect to see higher price tags for
              newer or centrally located buildings. This doesn’t mean that there
              aren’t bargains to be found though, since prices vary based on
              unit size, location, and included amenities.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.st-catharines:Houses for rent St Catharines Paragraph">
              <LinkText href={links.house}>
                Houses for rent in St. Catharines
              </LinkText>{' '}
              are located further from the city center in residential
              neighbourhoods. It’s typically more suitable for those with a
              higher budget, or who have multiple roommates. Both partial houses
              and entire houses for rent are common, as well as both new builds
              and older homes from the city’s earlier days.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Townhouses for rent CITY Paragraph">
              Townhouses are a popular form of housing in {{ cityName }} that
              combines the best qualities of both apartments and whole houses.
              Typically less expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in {{ cityName }}
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent CITY Paragraph">
              For those looking to find affordable rental housing in this
              increasingly expensive city, basements for rent in {{ cityName }}{' '}
              pose an attractive alternative. Since house prices are so
              expensive, homeowners will sometimes rent out their basements as a
              way to offset their mortgage payments. This makes it easy for
              renters to find relatively inexpensive basements for rent in all
              areas of the city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.st-catharines:Condos for rent St Catharines Paragraph">
              In more urban areas like Downtown St. Catharines,{' '}
              <LinkText href={links.apartment}>
                condos for rent in St. Catharines
              </LinkText>{' '}
              are a common sight. These types of rental accommodation are
              perfect for those who like to be in the centre of the action, and
              don’t need as much space. If you are looking to rent in the city
              centre though, be prepared for higher-than-average rent prices.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.st-catharines:Short term rentals St Catharines Paragraph">
              St. Catharines, with its diverse economy and the presence of
              institutions like Brock University and Niagara College, does
              attract individuals who may require short-term rental
              accommodations. However,{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText> are
              not the predominant market segment, as many students and working
              professionals seek longer-term stability. While there is a surge
              in demand for housing, especially around the commencement of
              school semesters, the city’s rental market is more geared towards
              traditional lease agreements. The availability of short-term
              rentals may be limited and often sought by individuals needing
              transitional housing or here for seasonal work. For many, the
              decision to opt for short-term rentals is influenced by factors
              like temporary work contracts, academic semesters, or personal
              flexibility requirements. As a result, it’s a good idea to think
              it through and{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are suitable
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals LOCATION 2 Paragraph">
              {{ cityName }} is known for its inexpensive rent, making it
              relatively easy to find something within your budget. If you’re
              looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in {{ cityNameWithState }}
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in {{ cityName }}{' '}
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.st-catharines:Living in St Catharines Paragraph">
          Living in St. Catharines, Ontario offers a harmonious blend of natural
          beauty and urban comfort. Nestled in the Niagara Region, the city
          provides access to Lake Ontario’s shores and the scenic Niagara
          Escarpment. St. Catharines fosters a welcoming community and hosts
          cultural events. While not part of the GTA, its proximity to{' '}
          <LinkText href="/rental-listings/city/toronto">Toronto</LinkText>{' '}
          ensures connectivity and amenities. The city’s affordability, when
          compared to the rest of Canada, makes it an appealing choice for a
          well-rounded lifestyle. See our blog about the{' '}
          <LinkText href="https://liv.rent/blog/renters/cost-of-living-comparison-in-canadian-cities/">
            cost of living comparison in Canada
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.st-catharines:Popular Neighbourhoods St Catharines Paragraph">
          St. Catharines, often referred to as &quot;The Garden City,&quot; is
          known for its neighbourhoods that offer diverse lifestyles, from urban
          living to quieter, family-friendly environments. Some of the most
          popular neighbourhoods in St. Catharines, Ontario include Downtown St.
          Catherines, Port Dalhousie, North End, Old Glenbridge, Merritton,
          Secord Woods, Westdale, Grantham, and Facer District.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.st-catharines:Local Landmarks St Catharines Paragraph">
          St. Catharines, a city rich in history and culture, boasts several
          landmarks that capture its unique heritage and natural beauty. One
          prominent site is the Welland Canal, a renowned series of locks
          facilitating international shipping between Lake Ontario and Lake
          Erie, often frequented by tourists and residents alike. For nature
          enthusiasts, the Montebello Park, designed by the same mind behind New
          York’s Central Park, Frederick Law Olmsted, offers lush greenery and
          hosts the city’s annual Grape and Wine Festival. The historic Port
          Dalhousie, home to the antique Lakeside Park Carousel, provides a
          nostalgic glimpse of the city’s past, while the Rodman Hall Art
          Centre, housed in a 19th-century mansion, highlights an eclectic art
          collection and the region’s commitment to cultural preservation. These
          landmarks represent just a sliver of St. Catharines’ offerings,
          combining historical depth with natural allure, and solidifying the
          city as a diverse and vibrant community hub.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default StCatharines
